import React, { createContext, useContext, ReactNode } from 'react';

export interface GameProgress {
  unlockedLevel: number;
  completedLevels: number[];
  levelScores: Record<number, number>;
  levelStars: Record<number, number>;
  currentSet: number;
}

export interface StorageMethods {
  getGameSpecificStorageKey: () => string;
  saveProgressSafely: (storageKey: string, data: GameProgress) => boolean;
  loadProgressSafely: (storageKey: string, defaultProgress: GameProgress) => GameProgress;
  clearProgressSafely: (storageKey: string) => boolean;
}

export interface StorageProviderProps {
  children: ReactNode;
}

const StorageContext = createContext<StorageMethods | undefined>(undefined);

const getGameSpecificStorageKey = (): string => {
  try {
    const url = window.location.href;
    const gameIdMatch = url.match(/\/games\/game\/[^/]+\/([^/]+)\//); 
    const gameId = gameIdMatch ? gameIdMatch[1] : null;

    if (!gameId) {
      console.warn('Could not extract game ID from URL, using fallback');
      return `gameProgress_${crypto.randomUUID()}`;
    }

    return `gameProgress_${gameId}`;
  } catch (error) {
    console.warn('Failed to generate game-specific storage key, using fallback:', error);
    return `gameProgress_${crypto.randomUUID()}`;
  }
};

const saveProgressSafely = (storageKey: string, data: GameProgress): boolean => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Failed to save progress to localStorage:', error);
    return false;
  }
};

const loadProgressSafely = (storageKey: string, defaultProgress: GameProgress): GameProgress => {
  try {
    const savedData = localStorage.getItem(storageKey);
    if (!savedData) {
      return defaultProgress;
    }

    const progress = JSON.parse(savedData);

    const validatedProgress = {
      ...defaultProgress,
      ...progress
    };

    if (!Array.isArray(validatedProgress.completedLevels)) {
      validatedProgress.completedLevels = [];
    }

    if (typeof validatedProgress.unlockedLevel !== 'number') {
      validatedProgress.unlockedLevel = defaultProgress.unlockedLevel;
    }

    if (typeof validatedProgress.levelScores !== 'object' || validatedProgress.levelScores === null) {
      validatedProgress.levelScores = {};
    }

    if (typeof validatedProgress.levelStars !== 'object' || validatedProgress.levelStars === null) {
      validatedProgress.levelStars = {};
    }

    return validatedProgress;

  } catch (error) {
    console.error('Failed to load progress from localStorage, using defaults:', error);
    try {
      localStorage.removeItem(storageKey);
    } catch (cleanupError) {
      console.error('Failed to clean up corrupted localStorage:', cleanupError);
    }
    return defaultProgress;
  }
};

const clearProgressSafely = (storageKey: string): boolean => {
  try {
    localStorage.removeItem(storageKey);
    return true;
  } catch (error) {
    console.error('Failed to clear progress from localStorage:', error);
    return false;
  }
};

export const StorageProvider = ({ children }: StorageProviderProps) => {
  const storageMethods: StorageMethods = {
    getGameSpecificStorageKey,
    saveProgressSafely,
    loadProgressSafely,
    clearProgressSafely
  };

  return (
    <StorageContext.Provider value={storageMethods}>
      {children}
    </StorageContext.Provider>
  );
};

export const useStorage = (): StorageMethods => {
  const context = useContext(StorageContext);
  if (context === undefined) {
    throw new Error('useStorage must be used within a StorageProvider');
  }
  return context;
};

export default StorageProvider;